import { Injectable, Type } from '@angular/core';
import {
	MatLegacyDialog as MatDialog,
	MatLegacyDialogConfig as MatDialogConfig,
	MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { CLASSES, SELECTORS } from '@root/app.config';
import { getModalsConfig } from '@core/services/modal/modal.config';
import { DialogOptions } from '@root/app.interfaces';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Injectable({
	providedIn: 'root',
})
export class ModalService {
	constructor(
		private dialog: MatDialog,
		private breakpointObserver: BreakpointObserver,
	) {}

	private readonly liftedUpModalIds: string[] = [];

	liftUpModal<T>(dialog: MatDialogRef<T>): void {
		const componentType = dialog.componentRef?.componentType as Type<T> & { ID?: string };
		const id = componentType?.ID ?? dialog.id;
		this.toggleLiftUpClass(true, id);

		dialog.afterClosed().subscribe(this.toggleLiftUpClass.bind(this, false, id));
	}

	private toggleLiftUpClass(status: boolean, modalId: string): void {
		const container = document.querySelector(SELECTORS.overlay_container) as HTMLElement;

		if (!container) {
			return;
		}

		if (status) {
			if (!this.liftedUpModalIds.includes(modalId)) {
				this.liftedUpModalIds.push(modalId);
			}
			container.classList.add(CLASSES.internal);
		} else {
			this.liftedUpModalIds.splice(this.liftedUpModalIds.indexOf(modalId), 1);
			if (!this.liftedUpModalIds.length) {
				container.classList.remove(CLASSES.internal);
			}
		}
	}

	getConfig<T>(id: string, data?: T): MatDialogConfig {
		const config = getModalsConfig()[id];

		if (data) {
			config.data = data;
		}

		return config;
	}

	openResponsiveDialog<D>(options: DialogOptions<D>): MatDialogRef<any> {
		const { component, data, width = '711px', panelClass = 'mc-modal__internal', ...config } = options;

		return this.dialog.open(component, {
			...{ width: this.getDialogWidth(width), data, panelClass },
			...config,
		});
	}

	isModalOpen<T>(component: Type<T>): boolean {
		return this.dialog.openDialogs.some((dialogRef) => dialogRef.componentInstance instanceof component);
	}

	private getDialogWidth(width: string): string {
		this.breakpointObserver.observe([Breakpoints.Small, Breakpoints.XSmall]).subscribe((result) => {
			if (result.matches) {
				width = 'auto';
			}
		});

		return width;
	}
}
